import { AsyncPipe, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, distinctUntilChanged, filter, map, Observable, switchMap } from 'rxjs';

import {
  CustomTemplateRendererComponent,
  CustomTemplateRendererService,
  replaceAllTokensForRender,
} from '@blaze-runtime/html-template-renderer-feature';
import {
  getBackground,
  ITEMS_CONTAINER_ACTION_EDIT,
  ITEMS_CONTAINER_ACTION_VIEW,
  ITEMS_CONTAINER_ACTION_VIEW_DIALOG,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
  RowClickActions,
} from '@blaze-shared/elements-domain';
import { convertBlazeSchemaToElementSchema, DatasetRow } from '@blaze-shared/schema-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import { isEqual } from '@blaze-shared/shared-util';

import { ElementHeaderComponent } from '../../components/element-header/element-header.component';
import { CanvasElementBaseComponent } from '../__base/base.component';
import { ElementDataActions } from '../__base/element-data-actions';

@UntilDestroy()
@Component({
  selector: 'be-html-card',
  imports: [AsyncPipe, CustomTemplateRendererComponent, ElementHeaderComponent, NgStyle],
  providers: [ElementDataActions<'htmlCard'>],
  templateUrl: './html-card.component.html',
  styleUrls: ['./html-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HtmlCardComponent
  extends CanvasElementBaseComponent<'htmlCard'>
  implements OnChanges, OnInit
{
  private readonly customTemplateRendererService = inject(CustomTemplateRendererService);
  private readonly elementDataActions = inject(ElementDataActions<'htmlCard'>);

  @Input() value?: DatasetRow[];

  private readonly dataValue = this.getDataInput('value');
  private readonly value$$ = new BehaviorSubject<DatasetRow[] | undefined>(undefined);

  readonly templateCode$: Observable<string>;
  readonly context$: Observable<DatasetRow>;
  readonly clickAction = signal<RowClickActions | undefined>(undefined);

  icon = '';
  label = '';
  labelEnabled = false;
  wrapperStyles: Record<string, string | undefined> | null = null;

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);

    this.elementDataActions.props = toSignal(this.props$);
    this.elementDataActions.dataValue = this.dataValue;
    this.elementDataActions.elementId = toSignal(this.elementId$);
    this.elementDataActions.elementType = this.elementType;

    // Schema is `undefined` when the element is not bound - the template is then rendered as-is.
    this.templateCode$ = this.props$.pipe(
      map((props) => props?.content?.htmlCard?.template?.code ?? ''),
      distinctUntilChanged(),
      switchMap((code) =>
        this.dataValue.schema$.pipe(
          map((schema) => this.customTemplateRendererService.createTokens(schema)),
          map((tokens) => replaceAllTokensForRender(code, tokens)),
        ),
      ),
      distinctUntilChanged(),
    );

    this.context$ = this.value$$.pipe(
      map((rows) => rows?.[0] ?? {}),
      distinctUntilChanged(isEqual),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.value$$.next(this.value);
    }

    if (changes['props']) {
      const { content } = this.props ?? {};
      this.label = content?.title?.label ?? '';
      this.icon = content?.title?.icon ?? '';
      this.labelEnabled = content?.title?.enabled ?? false;
      this.clickAction.set(content?.rowClick?.enabled ? content.rowClick.action : undefined);
      this.updateStyles();
    }
  }

  ngOnInit(): void {
    if (!this.bindingResults?.['value']) {
      this.setDefaultEventSchema('action');
    }

    this.dataValue.schema$.pipe(filter(Boolean), untilDestroyed(this)).subscribe((schema) => {
      this.setDefaultEventSchema(
        'action',
        convertBlazeSchemaToElementSchema(schema, this.elementId, 'action'),
      );
    });
  }

  onCardClick(event: Event): void {
    const action = this.clickAction();

    if (!action) {
      return;
    }

    this.elementDataActions.onAction({
      event,
      action: { id: action, label: this.getActionLabel(action) },
      isBulkAction: false,
      dataRow: this.value?.[0] ?? {},
      output: this.output,
      dataRowSchema: this.getEventSchema('action'),
    });
  }

  getDataSample(): unknown {
    return this.value?.[0];
  }

  private getActionLabel(action: RowClickActions): string {
    if (action === ITEMS_CONTAINER_ACTION_EDIT) {
      return 'Edit';
    }

    if (action === ITEMS_CONTAINER_ACTION_VIEW || action === ITEMS_CONTAINER_ACTION_VIEW_DIALOG) {
      return 'View';
    }

    return 'Row click';
  }

  private updateStyles(): void {
    const { style } = this.props ?? {};
    const { general, label, titleBlock, container } = style ?? {};
    if (!style || !general || !label || !titleBlock || !container) {
      return;
    }

    this.wrapperStyles = {
      ...makeContainerVisualEffectStyles(container.visualEffect),
      ...makeBorderStyles(container.outline, container.outlineWidth),
      padding: general.offset.padding,
      background: getBackground(container.fill),
    };

    safeDetectChanges(this.cdr);
  }
}
